import React, { createContext, useContext, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { io } from 'socket.io-client';
import { useAuth } from './AuthContext';

const ChatContext = createContext(null);

const API_URL =
  process.env.REACT_APP_API_URL || `${window.location.origin}/api`;

const SOCKET_URL = new URL(API_URL).origin;

export function ChatProvider({ children }) {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [socket, setSocket] = useState(null);
  const storageKey = user?.user_id ? `chat-unread-${user.user_id}` : null;
  const [unreadCount, setUnreadCount] = useState(0);
  const [toast, setToast] = useState(null);

  const fetchUnreadCount = async () => {
    if (!user?.user_id) return;
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/chat/unread`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) {
        setUnreadCount(data.count);
      }
    } catch (error) {
      console.error('Failed to fetch unread count:', error);
    }
  };

  useEffect(() => {
    fetchUnreadCount();
  }, [user?.user_id, location.pathname]); // Refresh when navigating (e.g. leaving chat)

  useEffect(() => {
    try {
      if ('Notification' in window && window.isSecureContext) {
        if (Notification.permission !== 'granted' && Notification.permission !== 'denied') {
          Notification.requestPermission().catch(() => {});
        }
      }
    } catch (e) {
      console.log('Native notifications not supported or blocked:', e);
    }
  }, []);

  useEffect(() => {
    if (!user?.user_id) return undefined;

    const token = localStorage.getItem('token');
    const chatSocket = io(SOCKET_URL, {
      transports: ["websocket", "polling"],
      withCredentials: true,
      auth: { token },
    });
    chatSocket.emit('user_online');
    setSocket(chatSocket);

    return () => {
      chatSocket.disconnect();
      setSocket(null);
    };
  }, [user?.user_id]);

  useEffect(() => {
    if (!socket) return undefined;

    const handleReceivedMessage = (message) => {
      fetchUnreadCount();

      let text = message?.content || 'New message';
      if (typeof text === 'string' && text.match(/^\[IMAGE:(.*)\]$/)) {
        text = '📷 Sent an image';
      }

      // Ensure we don't show toast if they are the sender
      if (message?.sender_id === user?.user_id) {
        return; 
      }

      const isMention = Boolean(message?.isMention || (Array.isArray(message?.mentioned_user_ids) && message.mentioned_user_ids.includes(Number(user?.user_id))));

      // Show in-app floating notification (Custom Toast)
      // Showing it even if they are on chat page, just in case they are looking at a different chat.
      setToast({
        id: Date.now(),
        title: isMention
          ? `🔔 ${message?.sender_name || 'Someone'} mentioned you in ${message?.team_name || 'Team Chat'}!`
          : (message?.sender_name ? `New message from ${message.sender_name}` : 'New Message'),
        body: text,
        isMention,
      });
      setTimeout(() => setToast(null), isMention ? 6000 : 4000);

      // Show native browser notification safely
      try {
        if ('Notification' in window && Notification.permission === 'granted' && document.hidden) {
          const notifTitle = isMention
            ? `Mentioned by ${message?.sender_name || 'Someone'} in ${message?.team_name || 'Team Chat'}`
            : (message?.sender_name ? `Message from ${message.sender_name}` : 'New Chat Message');
          const notif = new Notification(notifTitle, {
            body: text,
          });
          
          notif.onclick = () => {
            window.focus();
            navigate('/app/chat');
            notif.close();
          };
        }
      } catch (e) {
        console.log('Failed to show native notification:', e);
      }
    };

    const handleUserMentioned = (data) => {
      fetchUnreadCount();
      const content = data?.message?.content || 'You were mentioned in a message.';
      setToast({
        id: Date.now(),
        title: `🔔 ${data?.sender_name || 'Someone'} mentioned you in ${data?.team_name || 'Team Chat'}!`,
        body: content,
        isMention: true,
      });
      setTimeout(() => setToast(null), 6000);
    };

    socket.on('receive_message', handleReceivedMessage);
    socket.on('user_mentioned', handleUserMentioned);
    return () => {
      socket.off('receive_message', handleReceivedMessage);
      socket.off('user_mentioned', handleUserMentioned);
    };
  }, [socket, user?.user_id, navigate]);

  return (
    <ChatContext.Provider value={{ socket, unreadCount, fetchUnreadCount }}>
      {children}
      {toast && (
        <div 
          onClick={() => {
             navigate('/app/chat');
             setToast(null);
          }}
          className="glass-panel"
          style={{
            position: 'fixed',
            bottom: '24px',
            right: '24px',
            padding: '16px 20px',
            borderRadius: 'var(--radius)',
            zIndex: 9999,
            cursor: 'pointer',
            minWidth: '280px',
            maxWidth: '350px',
            animation: 'slideIn 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            boxShadow: 'var(--shadow)',
            border: '1px solid var(--border-bright)'
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '6px' }}>
            <div style={{ fontWeight: '600', color: 'var(--text-primary)' }}>
              {toast.title}
            </div>
            <button 
              onClick={(e) => {
                e.stopPropagation();
                setToast(null);
              }}
              style={{
                background: 'transparent',
                border: 'none',
                color: 'var(--text-muted)',
                fontSize: '18px',
                cursor: 'pointer',
                lineHeight: 1,
                marginLeft: '12px'
              }}
            >
              ×
            </button>
          </div>
          <div style={{ color: 'var(--text-secondary)', fontSize: '14px', WebkitLineClamp: 2, display: '-webkit-box', WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
            {toast.body}
          </div>
        </div>
      )}
    </ChatContext.Provider>
  );
}

export function useChat() {
  const context = useContext(ChatContext);
  if (!context) throw new Error('useChat must be used inside ChatProvider');
  return context;
}
